import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Layout from "../layouts/Layout";
import {
  API_BASE_URL,
  FONT_HEADING,
  FONT_UI,
  SKIN_THEMES,
} from "../layouts/constants";

const STATUS_OPTIONS = ["planned", "active", "stopped", "abandoned"];

function useSkin() {
  const skinId = localStorage.getItem("skinTheme") || "navy";
  return SKIN_THEMES.find((s) => s.id === skinId) || SKIN_THEMES[0];
}

function getToken() {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("adminToken")
  );
}

function formatIST(value) {
  if (!value) return "";

  const raw = String(value).trim();

  const match = raw.match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/
  );

  if (!match) return raw;

  const [, yyyy, mm, dd, hh, min, ss = "00"] = match;

  let hour = parseInt(hh, 10);
  const ampm = hour >= 12 ? "PM" : "AM";

  hour = hour % 12;
  if (hour === 0) hour = 12;

  return `${dd}-${mm}-${yyyy} ${String(hour).padStart(
    2,
    "0"
  )}:${min}:${ss} ${ampm}`;
}

function valueOrDash(value) {
  return value === null || value === undefined || value === "" ? "-" : value;
}

export default function SentinelEscortList() {
  const skin = useSkin();
  const navigate = useNavigate();
  const location = useLocation();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const [q, setQ] = useState(params.get("q") || "");
  const [status, setStatus] = useState(params.get("status") || "");
  const [page, setPage] = useState(Number(params.get("page") || 1));

  const [rows, setRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);

  const [alert, setAlert] = useState({ type: "", message: "" });

  const [dashboardData, setDashboardData] = useState({
    company_name: "",
    licensedFor: "",
    admin_active: 0,
    company_address: "",
    company_phone: "",
    admin_email: "",
    company_logo: "",
    user_map: "",
  });

  const recordsPerPage = 10;

  const routePage = useMemo(() => "/sentinel_add_Route", []);

  const sentinelEscortPage = useMemo(() => "/sentinel_add_escort", []);

  const sentinelTrackingPage = "/sentinel_escort_live_tracking";

  const authHeaders = () => {
    const token = getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const buildQueryString = (
    nextPage = page,
    nextQ = q,
    nextStatus = status
  ) => {
    const search = new URLSearchParams();

    search.set("page", String(nextPage));
    search.set("limit", String(recordsPerPage));

    if (nextQ.trim()) search.set("q", nextQ.trim());
    if (nextStatus.trim()) search.set("status", nextStatus.trim());

    return search.toString();
  };

  const updateUrl = (nextPage = page, nextQ = q, nextStatus = status) => {
    const query = buildQueryString(nextPage, nextQ, nextStatus);
    navigate(`/sentinel_escort_list?${query}`, { replace: false });
  };

  useEffect(() => {
    const token = getToken();
    const isLoggedIn = localStorage.getItem("isLoggedIn");
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");

    if (!token || !isLoggedIn) {
      navigate("/");
      return;
    }

    const local = {
      company_name: localStorage.getItem("company_name") || userData.company_name || "",
      licensedFor: localStorage.getItem("licensedFor") || userData.licensedFor || "",
      admin_active: Number(localStorage.getItem("admin_active") ?? userData.admin_active ?? 0),
      company_address: localStorage.getItem("company_address") || userData.company_address || "",
      company_phone: localStorage.getItem("company_phone") || userData.company_phone || "",
      admin_email: localStorage.getItem("admin_email") || userData.admin_email || "",
      company_logo: localStorage.getItem("company_logo") || userData.company_logo || "",
      user_map: localStorage.getItem("user_map") || userData.user_map || "",
    };

    setDashboardData(local);
    fetchPageData(local, token);
  }, [navigate]);

  useEffect(() => {
    setQ(params.get("q") || "");
    setStatus(params.get("status") || "");
    setPage(Number(params.get("page") || 1));
  }, [params]);

  useEffect(() => {
    if (!loading) {
      fetchSentinelEscortRuns();
    }
  }, [location.search]);

  useEffect(() => {
    if (!alert.message) return;

    const timer = setTimeout(() => {
      setAlert({ type: "", message: "" });
    }, 5000);

    return () => clearTimeout(timer);
  }, [alert.message]);

  async function fetchPageData(local, token) {
    try {
      setLoading(true);

      const dashboardRes = await fetch(`${API_BASE_URL}/dashboard/admin/dashboard`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!dashboardRes.ok) {
        throw new Error("Failed to fetch dashboard");
      }

      const dashboardResult = await dashboardRes.json();
      const apiData = dashboardResult?.data || {};
      const companyDetails = apiData?.companyDetails || {};

      const mergedDashboard = {
        ...local,
        company_name: companyDetails.company_name ?? local.company_name ?? "",
        licensedFor: companyDetails.licensedFor ?? local.licensedFor ?? "",
        admin_active: Number(companyDetails.admin_active ?? local.admin_active ?? 0),
        company_address: companyDetails.company_address ?? local.company_address ?? "",
        company_phone: companyDetails.company_phone ?? local.company_phone ?? "",
        admin_email: companyDetails.admin_email ?? local.admin_email ?? "",
        company_logo: companyDetails.company_logo ?? local.company_logo ?? "",
        user_map: companyDetails.user_map ?? local.user_map ?? "",
      };

      setDashboardData(mergedDashboard);

      await fetchSentinelEscortRuns();
    } catch (error) {
      console.error("Sentinel escort list page error:", error);

      setAlert({
        type: "error",
        message: error.message || "Failed to load sentinel escort list.",
      });
    } finally {
      setLoading(false);
    }
  }

  async function fetchSentinelEscortRuns() {
    try {
      setTableLoading(true);

      const currentParams = new URLSearchParams(window.location.search);
      const currentPage = Number(currentParams.get("page") || page || 1);
      const currentQ = currentParams.get("q") ?? q;
      const currentStatus = currentParams.get("status") ?? status;

      const query = buildQueryString(currentPage, currentQ, currentStatus);

      const res = await fetch(`${API_BASE_URL}/sentinel_escort/runs?${query}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
      });

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to fetch sentinel escort runs");
      }

      const apiRows = Array.isArray(data.data)
        ? data.data
        : Array.isArray(data.rows)
        ? data.rows
        : Array.isArray(data.data?.runs)
        ? data.data.runs
        : [];

      setRows(apiRows);
      setTotalRecords(Number(data.totalRecords || data.total_records || 0));
      setTotalPages(Number(data.totalPages || data.total_pages || 1));
    } catch (err) {
      console.error("Fetch sentinel escort runs error:", err);

      setRows([]);
      setTotalRecords(0);
      setTotalPages(1);

      setAlert({
        type: "error",
        message: err.message || "Something went wrong",
      });
    } finally {
      setTableLoading(false);
    }
  }

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    updateUrl(1, q, status);
  };

  const handleClear = () => {
    setQ("");
    setStatus("");
    setPage(1);
    navigate("/sentinel_escort_list");
  };

  const handlePageChange = (nextPage) => {
    if (nextPage < 1 || nextPage > totalPages) return;
    setPage(nextPage);
    updateUrl(nextPage, q, status);
  };

  const handleDelete = async (runId) => {
    const ok = window.confirm(
      `Delete sentinel escort run #${runId}? This will remove the run and all its points/events.`
    );

    if (!ok) return;

    try {
      const res = await fetch(`${API_BASE_URL}/sentinel_escort/runs/${runId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
      });

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Delete failed");
      }

      setAlert({
        type: "success",
        message: `Sentinel escort run #${runId} and its tracking data were deleted.`,
      });

      fetchSentinelEscortRuns();
    } catch (err) {
      setAlert({
        type: "error",
        message: err.message || "Delete failed",
      });
    }
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const items = [];
    const start = Math.max(1, page - 2);
    const end = Math.min(totalPages, page + 2);

    if (page > 1) {
      items.push(
        <PageButton key="prev" onClick={() => handlePageChange(page - 1)}>
          ‹ Prev
        </PageButton>
      );
    }

    if (start > 1) {
      items.push(
        <PageButton key={1} onClick={() => handlePageChange(1)}>
          1
        </PageButton>
      );

      if (start > 2) {
        items.push(
          <span key="dots-start" className="text-slate-500 px-1">
            …
          </span>
        );
      }
    }

    for (let i = start; i <= end; i += 1) {
      items.push(
        <PageButton
          key={i}
          active={i === page}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </PageButton>
      );
    }

    if (end < totalPages) {
      if (end < totalPages - 1) {
        items.push(
          <span key="dots-end" className="text-slate-500 px-1">
            …
          </span>
        );
      }

      items.push(
        <PageButton key={totalPages} onClick={() => handlePageChange(totalPages)}>
          {totalPages}
        </PageButton>
      );
    }

    if (page < totalPages) {
      items.push(
        <PageButton key="next" onClick={() => handlePageChange(page + 1)}>
          Next ›
        </PageButton>
      );
    }

    return (
      <div className="flex justify-center items-center gap-2 mt-5 flex-wrap">
        {items}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex flex-col items-center justify-center">
        <div className="h-10 w-10 rounded-full border-4 border-blue-100 border-t-blue-600 animate-spin mb-3" />
        <p className="text-slate-500" style={{ fontFamily: FONT_UI }}>
          Loading sentinel escort runs...
        </p>
      </div>
    );
  }

  return (
    <Layout
      dashboardData={dashboardData}
      routePage={routePage}
      activePage="sentinel_escort"
      activeMenu="sentinel_escort"
    >
      <div className="min-h-screen text-slate-900" style={{ fontFamily: FONT_UI }}>
        <PageHeader
          skin={skin}
          onNewEscort={() => navigate(sentinelEscortPage)}
          onRouteList={() => navigate("/sentinel_route_list")}
          onCreateRoute={() => navigate(routePage)}
        />

        {alert.message ? <Alert type={alert.type} message={alert.message} /> : null}

        <div className="bg-white border border-slate-100 rounded-2xl overflow-hidden shadow-sm">
          <div className="px-6 py-5 border-b border-slate-200 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3 font-bold text-lg text-slate-900">
              <i className="bx bx-list-ul text-blue-600 text-[22px]" />
              <span>All Sentinel Escort Runs</span>
            </div>

            <form
              className="flex items-center gap-2 flex-wrap"
              onSubmit={handleSearchSubmit}
            >
              <input
                type="text"
                placeholder="Search guard, title, from, to..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="h-10 min-w-[260px] px-3 rounded-xl border border-slate-200 bg-white text-sm text-slate-900 outline-none focus:border-blue-600 focus:ring-4 focus:ring-blue-100"
                style={{ fontFamily: FONT_UI }}
              />

              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="h-10 px-3 rounded-xl border border-slate-200 bg-white text-sm text-slate-900 outline-none focus:border-blue-600 focus:ring-4 focus:ring-blue-100"
                style={{ fontFamily: FONT_UI }}
              >
                <option value="">All statuses</option>
                {STATUS_OPTIONS.map((item) => (
                  <option key={item} value={item}>
                    {item.charAt(0).toUpperCase() + item.slice(1)}
                  </option>
                ))}
              </select>

              <button
                type="submit"
                className="h-10 px-4 rounded-xl text-white text-sm font-semibold inline-flex items-center justify-center gap-2 shadow-lg shadow-blue-500/20"
                style={{
                  background:
                    skin.gradient ||
                    "linear-gradient(135deg, #1e293b 0%, #2563eb 100%)",
                  fontFamily: FONT_UI,
                }}
              >
                <i className="bx bx-search" /> Search
              </button>

              {(q || status) && (
                <button
                  type="button"
                  className="h-10 px-4 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm font-semibold inline-flex items-center justify-center gap-2 hover:bg-slate-50"
                  onClick={handleClear}
                  style={{ fontFamily: FONT_UI }}
                >
                  <i className="bx bx-x" /> Clear
                </button>
              )}
            </form>
          </div>

          <div className="mx-6 my-4 px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 flex items-center gap-2 text-sm text-slate-500">
            <i className="bx bx-info-circle" />
            Total: <strong className="text-slate-900">{totalRecords}</strong> | Page{" "}
            {page} of {totalPages}
          </div>

          <div className="px-6 pb-6 overflow-x-auto">
            {tableLoading ? (
              <EmptyState
                iconClass="bx bx-loader-alt bx-spin"
                title="Loading sentinel escort runs..."
              />
            ) : rows.length > 0 ? (
              <>
                <table className="w-full min-w-[1120px] border border-slate-200 rounded-2xl overflow-hidden border-collapse">
                  <thead className="bg-slate-50">
                    <tr>
                      <Th>#</Th>
                      <Th>Run / Title</Th>
                      <Th>Guard</Th>
                      <Th>From → To</Th>
                      <Th>Status</Th>
                      <Th>Start / End</Th>
                      <Th>Points</Th>
                      <Th>Distance (m)</Th>
                      <Th>Actions</Th>
                    </tr>
                  </thead>

                  <tbody>
                    {rows.map((row, index) => {
                      const runId =
                        row.run_id ||
                        row.sentinel_run_id ||
                        row.escort_run_id ||
                        row.id;

                      const badge = String(row.status || "unknown").toLowerCase();
                      const serial = (page - 1) * recordsPerPage + index + 1;

                      const memberId =
                        row.member_id || row.guard_id || row.guard_member_id || "";

                      return (
                        <tr key={runId || index} className="hover:bg-slate-50">
                          <Td>{serial}</Td>

                          <Td>
                            <div className="mb-1">
                              <strong>#{valueOrDash(runId)}</strong> —{" "}
                              {valueOrDash(row.title)}
                            </div>

                            {/* <div className="font-mono text-xs text-slate-500">
                              Created: {formatIST(row.created_at) || "-"}
                            </div> */}
                          </Td>

                          <Td>
                            <span className="font-mono text-xs text-slate-500">
                              {valueOrDash(memberId)}
                            </span>

                            {row.guard_name || row.username ? (
                              <div className="mt-1 text-xs text-slate-700">
                                {row.guard_name || row.username}
                              </div>
                            ) : null}
                          </Td>

                          <Td>
                            <div>
                              <i className="bx bx-current-location" />{" "}
                              {valueOrDash(row.from_name)}
                            </div>
                            <div>
                              <i className="bx bx-navigation" />{" "}
                              {valueOrDash(row.to_name)}
                            </div>
                          </Td>

                          <Td>
                            <StatusBadge status={badge} />
                          </Td>

                          <Td>
                            <div className="font-mono text-xs text-slate-500">
                              {formatIST(row.start_time) || "-"}
                            </div>
                            <div className="font-mono text-xs text-slate-500">
                              {formatIST(row.end_time) || "-"}
                            </div>
                          </Td>

                          <Td>{Number(row.points_count || 0)}</Td>
                          <Td>{Number(row.distance_m || row.total_distance || 0)}</Td>

                          <Td>
  <div className="flex items-center gap-2">

    {!["active", "stopped"].includes(
      String(row.status || "").toLowerCase()
    ) && (
      <button
        type="button"
        onClick={() =>
          navigate(`${sentinelEscortPage}?edit_id=${runId}`)
        }
        className="w-10 h-10 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
      >
        ✏️
      </button>
    )}

    <button
      type="button"
      onClick={() =>
        navigate(
          `${sentinelTrackingPage}?member_id=${encodeURIComponent(
            memberId || ""
          )}&run_id=${runId}`
        )
      }
      className="w-10 h-10 bg-green-600 hover:bg-green-700 text-white rounded-lg"
    >
      🗺️
    </button>

    {!["active", "stopped"].includes(
      String(row.status || "").toLowerCase()
    ) && (
      <button
        type="button"
        onClick={() => handleDelete(runId)}
        className="w-10 h-10 bg-red-600 hover:bg-red-700 text-white rounded-lg"
      >
        🗑️
      </button>
    )}
  </div>
</Td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {renderPagination()}
              </>
            ) : (
              <EmptyState
                iconClass="bx bx-map"
                title="No Sentinel Escort Runs Found"
                description={
                  q || status
                    ? "No sentinel escorts match your current filters."
                    : "Start by creating a new sentinel escort run."
                }
              >
                {q || status ? (
                  <button
                    type="button"
                    className="h-10 px-4 rounded-xl text-white text-sm font-semibold"
                    style={{
                      background:
                        skin.gradient ||
                        "linear-gradient(135deg, #1e293b 0%, #2563eb 100%)",
                      fontFamily: FONT_UI,
                    }}
                    onClick={handleClear}
                  >
                    Show All
                  </button>
                ) : (
                  <button
                    type="button"
                    className="h-10 px-4 rounded-xl text-white text-sm font-semibold"
                    style={{
                      background:
                        skin.gradient ||
                        "linear-gradient(135deg, #1e293b 0%, #2563eb 100%)",
                      fontFamily: FONT_UI,
                    }}
                    onClick={() => navigate(sentinelEscortPage)}
                  >
                    New Sentinel Escort Run
                  </button>
                )}
              </EmptyState>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

function PageHeader({ skin, onNewEscort, onRouteList, onCreateRoute }) {
  return (
    <>
      <div className="mb-6">
        <h2
          className="m-0 text-[26px] font-normal text-slate-900 tracking-tight"
          style={{ fontFamily: FONT_HEADING }}
        >
          Sentinel Escort Runs
        </h2>

        <p className="mt-1 text-sm text-slate-500" style={{ fontFamily: FONT_UI }}>
          Manage sentinel escort runs, tracking points, status and live map view.
        </p>
      </div>

      <div className="bg-white rounded-2xl px-7 py-6 shadow-sm border border-slate-100 mb-6 flex justify-between items-center gap-4 flex-wrap">
        <div>
          <h3
            className="m-0 text-[22px] font-normal text-slate-900"
            style={{ fontFamily: FONT_HEADING }}
          >
            Sentinel Escort Route List
          </h3>

          <p className="mt-1 text-sm text-slate-500" style={{ fontFamily: FONT_UI }}>
            Search, view, edit, track, and delete sentinel escort run records.
          </p>
        </div>

        <div className="flex gap-3 flex-wrap">
          <button
            type="button"
            className="h-10 px-4 rounded-xl text-white text-sm font-semibold inline-flex items-center justify-center gap-2 shadow-lg shadow-blue-500/20"
            style={{
              background:
                skin.gradient ||
                "linear-gradient(135deg, #1e293b 0%, #2563eb 100%)",
              fontFamily: FONT_UI,
            }}
            onClick={onNewEscort}
          >
            <i className="bx bx-plus" /> New Sentinel Escort
          </button>

          <button
            type="button"
            className="h-10 px-4 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm font-semibold hover:bg-slate-50"
            style={{ fontFamily: FONT_UI }}
            onClick={onCreateRoute}
          >
            Create Route
          </button>

          <button
            type="button"
            className="h-10 px-4 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm font-semibold hover:bg-slate-50"
            style={{ fontFamily: FONT_UI }}
            onClick={onRouteList}
          >
            Route List
          </button>
        </div>
      </div>
    </>
  );
}

function Alert({ type, message }) {
  const isSuccess = type === "success";

  return (
    <div
      className={`mb-5 px-4 py-3.5 rounded-xl text-sm font-medium border flex items-center gap-2 ${
        isSuccess
          ? "bg-green-50 border-green-200 text-green-700"
          : "bg-red-50 border-red-200 text-red-700"
      }`}
      style={{ fontFamily: FONT_UI }}
    >
      <i className={`bx ${isSuccess ? "bx-check-circle" : "bx-error-circle"}`} />
      {message}
    </div>
  );
}

function Th({ children }) {
  return (
    <th
      className="text-left px-3 py-3.5 text-xs text-slate-700 border-b border-slate-200 whitespace-nowrap"
      style={{ fontFamily: FONT_UI }}
    >
      {children}
    </th>
  );
}

function Td({ children }) {
  return (
    <td
      className="px-3 py-3.5 text-sm border-b border-slate-200 align-middle"
      style={{ fontFamily: FONT_UI }}
    >
      {children}
    </td>
  );
}

function StatusBadge({ status }) {
  const cls =
    status === "active"
      ? "bg-green-100 text-green-700"
      : status === "planned"
      ? "bg-blue-100 text-blue-700"
      : status === "stopped"
      ? "bg-orange-100 text-orange-700"
      : status === "abandoned"
      ? "bg-red-100 text-red-700"
      : "bg-slate-100 text-slate-700";

  return (
    <span className={`px-3 py-1 rounded-full text-xs font-extrabold inline-flex ${cls}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

function IconButton({ children, className = "", ...props }) {
  return (
    <button
      type="button"
      {...props}
      className={`w-9 h-9 rounded-xl text-white text-lg inline-flex items-center justify-center ${className}`}
    >
      {children}
    </button>
  );
}

function PageButton({ children, active = false, ...props }) {
  return (
    <button
      type="button"
      {...props}
      className={`min-w-9 h-9 px-3 rounded-xl border text-sm font-bold ${
        active
          ? "bg-blue-600 text-white border-blue-600"
          : "bg-white text-slate-900 border-slate-200 hover:bg-slate-50"
      }`}
      style={{ fontFamily: FONT_UI }}
    >
      {children}
    </button>
  );
}

function EmptyState({ iconClass, title, description, children }) {
  return (
    <div className="min-h-[260px] border border-dashed border-slate-200 rounded-2xl flex flex-col items-center justify-center text-center p-8 text-slate-500">
      <i className={`${iconClass} text-5xl text-slate-300 mb-3`} />

      <h3
        className="m-0 mb-2 text-xl text-slate-900"
        style={{ fontFamily: FONT_HEADING }}
      >
        {title}
      </h3>

      {description ? (
        <p className="m-0 mb-4 text-sm" style={{ fontFamily: FONT_UI }}>
          {description}
        </p>
      ) : null}

      {children}
    </div>
  );
}